(function () {
  'use strict';

  var PAGE_SIZE = 24;
  var CACHE_MS = 5 * 60 * 1000;
  var GROUPS = [
    { key: 'artist', label: 'Artists', singular: 'Artist' },
    { key: 'type', label: 'Product types', singular: 'Product type' },
    { key: 'species', label: 'Species', singular: 'Species' },
    { key: 'character', label: 'Characters', singular: 'Character' }
  ];
  var stores = new Map();
  var instances = [];
  var sequence = 0;

  function node(tag, className, text) {
    var result = document.createElement(tag);
    if (className) result.className = className;
    if (text !== undefined) result.textContent = text;
    return result;
  }

  function text(value, maximum) {
    return typeof value === 'string' ? value.slice(0, maximum || 200) : '';
  }

  function productURL(value) {
    try {
      var url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[^/]+\/?$/i.test(url.pathname)) return '';
      return url.pathname;
    } catch (_) { return ''; }
  }

  function imageURL(value) {
    if (typeof value !== 'string' || !value) return '';
    try {
      var url = new URL(value, window.location.origin);
      var local = url.origin === window.location.origin;
      var shopify = url.hostname === 'cdn.shopify.com' || url.hostname === 'cdn.shopifycdn.net';
      if (url.protocol !== 'https:' || (!local && !shopify)) return '';
      if (!/\.(svg)(?:$|\?)/i.test(url.pathname)) url.searchParams.set('width', '480');
      return url.href;
    } catch (_) { return ''; }
  }

  function allowsEU() {
    return document.documentElement.classList.contains('fbp-eu-sale-allowed');
  }

  function isEUProduct(product) {
    return !!product.euOnly || text(product.handle).indexOf('eu-liquidation-') !== -1 ||
      (Array.isArray(product.tags) && product.tags.indexOf('eu-liquidation-sale') !== -1);
  }

  function regionalIndex(entry) {
    var allowed = allowsEU();
    if (!entry.indexes.has(allowed)) {
      entry.indexes.set(allowed, window.FBPSearchEngine.createIndex(entry.products.filter(function (product) {
        return allowed || !isEUProduct(product);
      })));
    }
    return entry.indexes.get(allowed);
  }

  async function fetchPage(base, page) {
    var url = new URL(base, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('The catalogue address must be on this store.');
    url.searchParams.set('page', String(page));
    var controller = new AbortController();
    var timeout = window.setTimeout(function () { controller.abort(); }, 12000);
    try {
      var response = await fetch(url.href, {
        // Shopify negotiates its built-in collection JSON for application/json,
        // bypassing this Liquid view. Request the view, then parse its JSON body.
        credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: controller.signal
      });
      if (!response.ok) throw new Error('Catalogue request failed.');
      var data = await response.json();
      if (!data || data.version !== 1 || data.page !== page || !Number.isInteger(data.pages) ||
          data.pages < 1 || data.pages > 200 || !Number.isInteger(data.total) || data.total < 0 ||
          !Number.isInteger(data.scanned) || data.scanned < 0 || data.scanned > 250 ||
          !Number.isInteger(data.excluded) || data.excluded < 0 ||
          !Array.isArray(data.products) || data.products.length > 250 ||
          data.products.length + data.excluded !== data.scanned) {
        throw new Error('The catalogue response was incomplete (page ' + page + '; fields: ' + Object.keys(data || {}).join(', ') + ').');
      }
      return data;
    } finally { window.clearTimeout(timeout); }
  }

  function catalogue(base) {
    var key = new URL(base, window.location.origin).href;
    var cached = stores.get(key);
    if (cached && (!cached.loadedAt || Date.now() - cached.loadedAt < CACHE_MS)) return cached;
    var entry = { indexes: new Map(), promise: null, loadedAt: 0 };
    entry.promise = (async function () {
      var first = await fetchPage(key, 1);
      var pages = new Array(first.pages);
      pages[0] = first;
      var next = 2;
      var failed = false;
      async function worker() {
        try {
          while (!failed && next <= first.pages) {
            var page = next++;
            var data = await fetchPage(key, page);
            if (data.pages !== first.pages || data.total !== first.total || data.currency !== first.currency) {
              throw new Error('The catalogue changed while loading. Please retry.');
            }
            pages[page - 1] = data;
          }
        } catch (error) {
          failed = true;
          throw error;
        }
      }
      var workers = await Promise.allSettled([worker(), worker()]);
      var failure = workers.find(function (workerResult) { return workerResult.status === 'rejected'; });
      if (failure) throw failure.reason;
      if (pages.reduce(function (sum, page) { return sum + page.scanned; }, 0) !== first.total) {
        throw new Error('The catalogue is missing a page. Please retry.');
      }
      var products = [];
      var seen = new Set();
      pages.forEach(function (page) {
        page.products.forEach(function (product) {
          var href = product && productURL(product.url);
          if (!href || !product.title || typeof product.title !== 'string' ||
              !product.handle || typeof product.handle !== 'string' || seen.has(href)) {
            throw new Error('The catalogue contains an invalid or repeated product. Please retry.');
          }
          seen.add(href);
          products.push(product);
        });
      });
      entry.products = products;
      entry.loadedAt = Date.now();
      return products;
    })().catch(function (error) {
      if (stores.get(key) === entry) stores.delete(key);
      throw error;
    });
    stores.set(key, entry);
    return entry;
  }

  function Search(root) {
    this.root = root;
    this.input = root.querySelector('[data-fbp-search-input]');
    this.form = root.querySelector('[data-fbp-search-form]');
    this.controls = root.querySelector('[data-fbp-search-controls]');
    this.status = root.querySelector('[data-fbp-search-status]');
    this.results = root.querySelector('[data-fbp-search-results]');
    this.more = root.querySelector('[data-fbp-search-more]');
    this.all = root.querySelector('[data-fbp-search-all]');
    this.clear = root.querySelector('[data-fbp-search-clear]');
    this.fallback = root.querySelector('[data-fbp-search-fallback]');
    if (!this.input || !this.form || !this.controls || !this.status || !this.results || !this.more) return;
    this.id = 'fbp-search-' + (++sequence);
    this.page = root.dataset.mode === 'page';
    this.query = '';
    this.filters = {};
    this.limit = PAGE_SIZE;
    this.index = null;
    this.entry = null;
    this.loading = false;
    this.failed = false;
    this.composing = false;
    this.ticket = 0;
    this.debounce = null;
    this.facets = new Map();
    this.input.maxLength = 200;
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    this.results.setAttribute('aria-label', 'Matching products');
    this.results.id = this.results.id || this.id + '-results';
    this.more.setAttribute('aria-controls', this.results.id);
    this.more.type = 'button';
    this.more.hidden = true;
    if (this.clear) this.clear.type = 'button';
    this.restore();
    this.bind();
    instances.push(this);
    root.classList.add('fbp-search-enhanced');
    if (this.page) this.load();
  }

  Search.prototype.restore = function () {
    var params = new URL(window.location.href).searchParams;
    this.query = this.page ? text(params.get('q') || '') : text(this.root.dataset.initialQuery);
    this.input.value = this.query;
    this.filters = {};
    if (this.page) {
      GROUPS.forEach(function (group) {
        var value = text(params.get('fbp_' + group.key));
        if (value) this.filters[group.key] = value;
      }, this);
    }
    this.limit = PAGE_SIZE;
    this.updateLinks();
    this.updateClear();
  };

  Search.prototype.url = function (native) {
    var url = new URL(this.root.dataset.searchUrl || '/search', window.location.origin);
    if (url.origin !== window.location.origin) url = new URL('/search', window.location.origin);
    url.search = '';
    url.searchParams.set('type', 'product');
    if (this.query.trim()) url.searchParams.set('q', this.query.trim());
    if (native) url.searchParams.set('view', 'fbp-native');
    else GROUPS.forEach(function (group) {
      if (this.filters[group.key]) url.searchParams.set('fbp_' + group.key, this.filters[group.key]);
    }, this);
    return url.pathname + url.search;
  };

  Search.prototype.history = function (replace) {
    if (!this.page) return;
    var url = this.url(false);
    if (window.location.pathname + window.location.search !== url) {
      window.history[replace ? 'replaceState' : 'pushState'](null, '', url);
    }
  };

  Search.prototype.updateLinks = function () {
    if (this.all) {
      this.all.href = this.url(this.failed);
      this.all.hidden = this.page && !this.failed;
      if (this.failed) this.all.textContent = 'Use standard search';
    }
    if (this.fallback) {
      this.fallback.hidden = !!this.index || this.query.trim() !== text(this.root.dataset.initialQuery).trim() || Object.keys(this.filters).length > 0;
      this.fallback.querySelectorAll('[data-fbp-search-native-link]').forEach(function (link) {
        link.href = this.url(true);
      }, this);
    }
  };

  Search.prototype.updateClear = function () {
    if (this.clear) this.clear.hidden = !this.query;
  };

  Search.prototype.bind = function () {
    var self = this;
    this.input.addEventListener('focus', function () { self.load(); self.updateFacetSpace(); });
    this.input.addEventListener('compositionstart', function () {
      self.composing = true;
      window.clearTimeout(self.debounce);
    });
    this.input.addEventListener('compositionend', function () { self.composing = false; self.changeQuery(); });
    this.input.addEventListener('input', function () {
      self.root.dataset.fbpSearchReady = 'false';
      if (!self.composing) self.changeQuery();
    });
    this.form.addEventListener('submit', function (event) {
      if (self.composing) { event.preventDefault(); return; }
      event.preventDefault();
      window.clearTimeout(self.debounce);
      self.query = text(self.input.value);
      self.limit = PAGE_SIZE;
      self.history();
      self.updateLinks();
      if (self.index) self.render();
      else self.load();
    });
    if (this.clear) this.clear.addEventListener('click', function () {
      self.input.value = '';
      self.changeQuery(true);
      self.input.focus();
    });
    this.more.addEventListener('click', function () {
      var previous = self.limit;
      self.limit += PAGE_SIZE;
      self.render(previous);
    });
    if (this.page) window.addEventListener('popstate', function () {
      window.clearTimeout(self.debounce);
      self.restore();
      if (self.index) { self.updateFacets(); self.render(); }
      else self.load();
    });
  };

  Search.prototype.changeQuery = function (immediate) {
    var self = this;
    window.clearTimeout(this.debounce);
    this.query = text(this.input.value);
    this.root.dataset.fbpSearchReady = 'false';
    this.limit = PAGE_SIZE;
    this.updateClear();
    this.updateLinks();
    function run() {
      self.history(true);
      if (self.index) self.render();
      else self.load();
    }
    if (immediate) run();
    else this.debounce = window.setTimeout(run, 80);
  };

  Search.prototype.load = async function () {
    if (this.loading || (this.index && !this.stale())) return;
    if (this.index) {
      this.index = null;
      this.root.style.minHeight = '';
      this.results.replaceChildren();
      this.controls.replaceChildren();
      this.facets.clear();
      this.selected = null;
      this.more.hidden = true;
      this.updateLinks();
    }
    var ticket = ++this.ticket;
    this.loading = true;
    this.failed = false;
    this.root.dataset.fbpSearchReady = 'false';
    this.root.setAttribute('aria-busy', 'true');
    this.status.textContent = 'Loading the complete product catalogue…';
    this.updateLinks();
    try {
      if (!window.FBPSearchEngine) throw new Error('The search engine has not loaded.');
      this.entry = catalogue(this.root.dataset.indexUrl || '/collections/all?view=fbp-search-index');
      await this.entry.promise;
      if (ticket !== this.ticket || !this.root.isConnected) return;
      this.index = await this.getIndex();
      this.buildFacets();
      this.render();
    } catch (error) {
      if (ticket !== this.ticket) return;
      console.warn('[FBP search] Catalogue unavailable:', error && error.message);
      this.failed = true;
      this.index = null;
      this.root.dataset.fbpSearchReady = 'false';
      this.root.classList.remove('fbp-search-ready');
      this.updateLinks();
      this.status.textContent = 'The full catalogue could not load. Retry or use standard search below.';
      var retry = node('button', 'fbp-search-retry', 'Retry loading');
      retry.type = 'button';
      var self = this;
      retry.addEventListener('click', function () { self.load(); });
      this.status.appendChild(retry);
      this.more.hidden = true;
      this.updateLinks();
    } finally {
      if (ticket === this.ticket) {
        this.loading = false;
        this.root.removeAttribute('aria-busy');
      }
    }
  };

  Search.prototype.stale = function () {
    return this.entry && this.entry.loadedAt && Date.now() - this.entry.loadedAt >= CACHE_MS;
  };

  Search.prototype.getIndex = async function () {
    await this.entry.promise;
    return regionalIndex(this.entry);
  };

  // Recheck synchronously before rendering, including after any async catalogue load.
  Search.prototype.syncRegion = function () {
    if (!this.entry || !this.entry.products) return;
    var next = regionalIndex(this.entry);
    if (next === this.index) return;
    this.index = next;
    this.limit = PAGE_SIZE;
    this.buildFacets();
  };

  Search.prototype.buildFacets = function () {
    var self = this;
    this.root.style.minHeight = '';
    this.controls.replaceChildren();
    this.facets.clear();
    var lead = node('p', 'fbp-search-facet-lead', 'Quick search by');
    var groups = node('div', 'fbp-search-facet-groups');
    GROUPS.forEach(function (group) {
      var values = (self.index.facets && self.index.facets[group.key]) || [];
      var details = node('details', 'fbp-search-facet');
      var summary = node('summary', '', group.label);
      var panel = node('div', 'fbp-search-facet-panel');
      var label = node('label', 'fbp-search-facet-label', 'Find ' + group.label.toLowerCase());
      var input = node('input', 'fbp-search-facet-input');
      input.type = 'search';
      input.autocomplete = 'off';
      input.id = self.id + '-' + group.key;
      input.placeholder = 'Filter ' + group.label.toLowerCase();
      input.maxLength = 100;
      label.htmlFor = input.id;
      var list = node('div', 'fbp-search-facet-options');
      list.setAttribute('role', 'group');
      list.setAttribute('aria-label', group.label);
      var empty = node('p', 'fbp-search-facet-empty', values.length ? 'No matching options.' : 'No options available.');
      empty.hidden = values.length > 0;
      var buttons = [];
      values.forEach(function (value) {
        var button = node('button', 'fbp-search-facet-option');
        button.type = 'button';
        button.setAttribute('aria-pressed', 'false');
        button.appendChild(node('span', '', text(value.label)));
        button.appendChild(node('span', 'fbp-search-facet-count', String(value.count)));
        button.addEventListener('click', function () {
          if (self.filters[group.key] === value.value) delete self.filters[group.key];
          else self.filters[group.key] = value.value;
          self.limit = PAGE_SIZE;
          self.query = text(self.input.value);
          self.updateFacets();
          self.history();
          self.render();
          details.open = false;
          summary.focus();
        });
        list.appendChild(button);
        buttons.push({ element: button, value: value.value, label: value.label, searchTerms: value.searchTerms || [value.label] });
      });
      input.addEventListener('input', function () {
        var query = window.FBPSearchEngine.normalize(input.value);
        var compactQuery = query.replace(/\s+/g, '');
        var matches = 0;
        buttons.forEach(function (button) {
          var show = button.searchTerms.some(function (term) {
            var labelText = window.FBPSearchEngine.normalize(term);
            return labelText.indexOf(query) !== -1 || labelText.replace(/\s+/g, '').indexOf(compactQuery) !== -1;
          });
          button.element.hidden = !show;
          if (show) matches++;
        });
        empty.hidden = matches > 0;
        self.updateFacetSpace();
      });
      details.addEventListener('toggle', function () {
        if (details.open) self.facets.forEach(function (facet) {
          if (facet.details !== details) facet.details.open = false;
        });
        self.updateFacetSpace();
      });
      panel.append(label, input, list, empty);
      details.append(summary, panel);
      groups.appendChild(details);
      self.facets.set(group.key, { details: details, summary: summary, buttons: buttons, group: group });
    });
    this.selected = node('div', 'fbp-search-selected');
    this.selected.setAttribute('aria-label', 'Selected filters');
    this.controls.append(lead, groups, this.selected);
    this.updateFacets();
  };

  Search.prototype.updateFacetSpace = function () {
    var panel = this.controls.querySelector('details[open] .fbp-search-facet-panel');
    if (!panel) { this.root.style.minHeight = ''; return; }
    var box = panel.getBoundingClientRect();
    if (!box.width) return;
    // Reserve the actual dropdown height, including wrapped labels at phone widths.
    this.root.style.minHeight = Math.ceil(box.bottom - this.root.getBoundingClientRect().top + 16) + 'px';
  };

  Search.prototype.updateFacets = function () {
    var self = this;
    if (!this.selected) return;
    this.selected.replaceChildren();
    this.facets.forEach(function (facet, key) {
      var selected = self.filters[key];
      if (key === 'artist' && selected) {
        var aliases = facet.buttons.filter(function (button) {
          return button.searchTerms.some(function (term) { return window.FBPSearchEngine.facetValue(term) === window.FBPSearchEngine.facetValue(selected); });
        });
        if (aliases.length === 1) selected = self.filters[key] = aliases[0].value;
      }
      var selectedLabel = selected;
      facet.buttons.forEach(function (button) {
        var active = button.value === selected;
        button.element.setAttribute('aria-pressed', String(active));
        if (active) selectedLabel = button.label;
      });
      facet.summary.textContent = facet.group.label + (selected ? ': ' + selectedLabel : '');
      if (selected) {
        var remove = node('button', 'fbp-search-chip', facet.group.singular + ': ' + selectedLabel + ' ×');
        remove.type = 'button';
        remove.setAttribute('aria-label', 'Remove ' + facet.group.singular.toLowerCase() + ' filter ' + selectedLabel);
        remove.addEventListener('click', function () {
          delete self.filters[key];
          self.limit = PAGE_SIZE;
          self.updateFacets();
          self.history();
          self.render();
          facet.summary.focus();
        });
        self.selected.appendChild(remove);
      }
    });
    if (Object.keys(this.filters).length) {
      var clear = node('button', 'fbp-search-reset', 'Clear filters');
      clear.type = 'button';
      clear.addEventListener('click', function () {
        self.filters = {};
        self.limit = PAGE_SIZE;
        self.updateFacets();
        self.history();
        self.render();
        self.input.focus();
      });
      this.selected.appendChild(clear);
    }
    this.selected.hidden = !Object.keys(this.filters).length;
  };

  Search.prototype.card = function (product, position) {
    var article = node('article', 'fbp-search-card' + (isEUProduct(product) ? ' fbp-eu-only' : ''));
    var link = node('a', 'fbp-search-card-link');
    link.href = productURL(product.url);
    var media = node('div', 'fbp-search-card-media');
    var src = imageURL(product.image);
    if (src) {
      var img = node('img', 'fbp-search-card-image');
      img.src = src;
      img.alt = '';
      img.width = 480;
      img.height = 480;
      img.loading = position < 4 ? 'eager' : 'lazy';
      img.decoding = 'async';
      img.addEventListener('error', function () {
        img.hidden = true;
        media.appendChild(node('span', 'fbp-search-image-missing', 'Image unavailable'));
      }, { once: true });
      media.appendChild(img);
    } else media.appendChild(node('span', 'fbp-search-image-missing', 'Image unavailable'));
    var body = node('div', 'fbp-search-card-body');
    body.appendChild(node('h3', 'fbp-search-card-title', text(product.title, 1000)));
    var artists = Array.isArray(product.artists) ? product.artists.filter(function (item) { return typeof item === 'string'; }) : [];
    var types = Array.isArray(product.types) ? product.types.filter(function (item) { return typeof item === 'string'; }) : [];
    var caption = [types[0], artists.length ? 'By ' + artists.join(', ') : ''].filter(Boolean).join(' · ');
    if (caption) body.appendChild(node('p', 'fbp-search-card-caption', caption));
    var price = node('p', 'fbp-search-card-price', text(product.price, 150));
    body.appendChild(price);
    if (product.available === false) body.appendChild(node('span', 'fbp-search-badge', 'Sold out'));
    if (isEUProduct(product)) body.appendChild(node('span', 'fbp-search-badge', 'EU delivery only'));
    link.append(media, body);
    article.appendChild(link);
    return article;
  };

  Search.prototype.render = function (focusAt) {
    if (!this.index) return;
    this.syncRegion();
    if (this.stale()) { this.load(); return; }
    var result = window.FBPSearchEngine.search(this.index, this.query, this.filters, this.limit);
    var fragment = document.createDocumentFragment();
    result.items.forEach(function (product, position) { fragment.appendChild(this.card(product, position)); }, this);
    this.results.replaceChildren(fragment);
    this.root.classList.add('fbp-search-ready');
    if (this.fallback) this.fallback.hidden = true;
    var count = result.total;
    this.root.dataset.fbpSearchReady = 'true';
    this.root.dataset.fbpSearchTotal = String(count);
    this.root.dataset.fbpSearchQuery = this.query;
    var noun = count === 1 ? 'product' : 'products';
    if (this.page) document.title = (this.query.trim() || 'Browse products') + ' — ' + count.toLocaleString() + ' ' + noun + ' | Furry Body Pillows';
    var phrase = count.toLocaleString() + ' ' + noun;
    if (this.query.trim()) phrase += ' for “' + this.query.trim() + '”';
    if (Object.keys(this.filters).length) phrase += ' with your filters';
    phrase += '.';
    if (result.corrected) phrase += ' Including close spelling matches.';
    if (!count) phrase += ' Try another word or clear a filter.';
    else if (result.items.length < count) phrase += ' Showing ' + result.items.length.toLocaleString() + '.';
    this.status.textContent = phrase;
    this.more.hidden = result.items.length >= count;
    this.more.textContent = 'Show ' + Math.min(PAGE_SIZE, Math.max(0, count - result.items.length)) + ' more products';
    if (this.all) this.all.textContent = 'Open full search page';
    this.updateLinks();
    this.updateClear();
    this.updateFacetSpace();
    this.root.dispatchEvent(new CustomEvent('fbp:search-results', {
      bubbles: true, detail: { query: this.query, total: count }
    }));
    if (typeof focusAt === 'number') {
      var nextCard = this.results.children[focusAt];
      if (nextCard) nextCard.querySelector('a').focus();
    }
  };

  function boot(scope) {
    scope.querySelectorAll('[data-fbp-search]:not([data-fbp-search-bound])').forEach(function (root) {
      root.dataset.fbpSearchBound = 'true';
      new Search(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);
  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
  window.addEventListener('resize', function () {
    instances.forEach(function (instance) { if (instance.root.isConnected) instance.updateFacetSpace(); });
  });
  function refreshRegion() {
    instances.forEach(function (instance) {
      if (!instance.index || !instance.root.isConnected) return;
      var previous = instance.index;
      instance.syncRegion();
      if (previous !== instance.index) instance.render();
    });
  }
  // The gate emits synchronously; the observer also covers existing theme integrations.
  document.addEventListener('fbp:eu-region-change', refreshRegion);
  new MutationObserver(refreshRegion).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
})();
